// src/components/LayoutShell.tsx
import React, { ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import { sendPasswordResetEmail } from "firebase/auth";
import { doc, onSnapshot } from "firebase/firestore";
import { theme } from "./UI";
import { auth, db } from "../firebase";
import { useAuth } from "../auth/AuthProvider";
import { ADMIN_ROLES, OPERATIONAL_ROLES, SUPER_ADMIN_UID, canAccessRole } from "../auth/roles";
import { clearLookupCache } from "../lib/lookup";
import blackBoxLogo from "../assets/3pAll_2022Logo_BlackBox.webp";

export interface LayoutShellProps {
  children?: ReactNode;
}

const navLinkBase: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  padding: "8px 12px",
  borderRadius: 999,
  textDecoration: "none",
  fontSize: 14,
  fontWeight: 500,
  color: theme.colors.muted,
  marginBottom: 4,
};

const navLinkActive: React.CSSProperties = {
  background: theme.colors.primarySoft,
  color: theme.colors.primary,
};

const navLinkHomeActive: React.CSSProperties = {
  ...navLinkActive,
  background: theme.colors.secondary,
};

const navDotStyle: React.CSSProperties = {
  width: 8,
  height: 8,
  borderRadius: 999,
  backgroundColor: theme.colors.secondaryBorder,
};

const navDotActiveStyle: React.CSSProperties = {
  backgroundColor: theme.colors.primary,
};

const LayoutShell: React.FC<LayoutShellProps> = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [userMenuMessage, setUserMenuMessage] = useState<string | null>(null);
  const [userMenuError, setUserMenuError] = useState<string | null>(null);
  const [sendingReset, setSendingReset] = useState(false);
  const sidebarRef = useRef<HTMLElement | null>(null);
  const menuButtonRef = useRef<HTMLButtonElement | null>(null);
  const userMenuRef = useRef<HTMLDivElement | null>(null);
  const userMenuButtonRef = useRef<HTMLButtonElement | null>(null);
  const { user: authUser, signOutUser } = useAuth();
  const location = useLocation();
  const role = authUser?.role;
  const canViewOperational = role ? canAccessRole(role, OPERATIONAL_ROLES) : false;
  const canViewAdminTools = role ? canAccessRole(role, ADMIN_ROLES) : false;
  const isSuperAdmin = authUser?.uid === SUPER_ADMIN_UID;
  const isClient = role === "client";
  const displayName = authUser?.displayName || authUser?.email || "";
  const avatarLetter = useMemo(() => {
    if (!displayName.trim()) return "U";
    return displayName.trim().charAt(0).toUpperCase();
  }, [displayName]);

  useEffect(() => {
    let sawInitialSnapshot = false;
    return onSnapshot(
      doc(db, "lookupMeta", "current"),
      () => {
        if (!sawInitialSnapshot) {
          sawInitialSnapshot = true;
          return;
        }
        clearLookupCache();
      },
      (err) => {
        console.warn("Failed to subscribe to lookup revision updates", err);
      }
    );
  }, []);

  const sectionAccent = useMemo(() => {
    const path = location.pathname || "";
    if (
      path === "/stock" ||
      path.startsWith("/stock-check") ||
      path.startsWith("/transfer") ||
      path.startsWith("/change") ||
      path.startsWith("/batch-transfer")
    ) {
      return "var(--ps-section-inventory)";
    }
    if (path.startsWith("/orders") || path.startsWith("/picks")) {
      return "var(--ps-section-sales)";
    }
    if (path.startsWith("/purchase")) {
      return "var(--ps-section-purchasing)";
    }
    if (path.startsWith("/reports")) {
      return "var(--ps-section-reports)";
    }
    if (path.startsWith("/build") || path.startsWith("/transactions") || path.startsWith("/lookups") || path.startsWith("/admin")) {
      return "var(--ps-section-admin)";
    }
    return "var(--ps-section-inventory)";
  }, [location.pathname]);

  useEffect(() => {
    if (!sidebarOpen) {
      return;
    }

    const handlePointerDown = (event: MouseEvent | TouchEvent) => {
      if (!sidebarRef.current || !menuButtonRef.current) {
        return;
      }
      const target = event.target;
      if (!(target instanceof Node)) {
        return;
      }
      if (window.matchMedia("(min-width: 961px)").matches) {
        return;
      }
      const clickedSidebar = sidebarRef.current.contains(target);
      const clickedToggle = menuButtonRef.current.contains(target);
      if (!clickedSidebar && !clickedToggle) {
        setSidebarOpen(false);
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("touchstart", handlePointerDown);

    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("touchstart", handlePointerDown);
    };
  }, [sidebarOpen]);

  useEffect(() => {
    if (!userMenuOpen) {
      return;
    }

    const handlePointerDown = (event: MouseEvent | TouchEvent) => {
      if (!userMenuRef.current || !userMenuButtonRef.current) {
        return;
      }
      const target = event.target;
      if (!(target instanceof Node)) {
        return;
      }
      const clickedMenu = userMenuRef.current.contains(target);
      const clickedButton = userMenuButtonRef.current.contains(target);
      if (!clickedMenu && !clickedButton) {
        setUserMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("touchstart", handlePointerDown);

    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("touchstart", handlePointerDown);
    };
  }, [userMenuOpen]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      const active = document.activeElement;
      if (
        active instanceof HTMLInputElement ||
        active instanceof HTMLTextAreaElement ||
        active instanceof HTMLSelectElement
      ) {
        return;
      }
      const main = document.querySelector(".ps-shell-content");
      const input = main?.querySelector<HTMLInputElement>(
        [
          "input[data-auto-focus-main='true']",
          "input.ps-search-input",
          "input[type='search']",
          "input[placeholder*='search' i]",
          "input[placeholder*='scan' i]",
        ].join(",")
      );
      if (!input || input.disabled || input.readOnly) return;
      input.focus();
    }, 0);

    return () => window.clearTimeout(timer);
  }, [location.pathname]);

  useEffect(() => {
    const shouldAutoSelect = (input: HTMLInputElement) => {
      if (input.dataset.autoselect === "off") {
        return false;
      }
      if (input.disabled || input.readOnly) {
        return false;
      }
      const type = (input.getAttribute("type") || "").toLowerCase();
      if (type === "number") {
        return true;
      }
      const inputMode = (input.getAttribute("inputmode") || "").toLowerCase();
      if (inputMode === "numeric" || inputMode === "decimal") {
        return true;
      }
      const haystack = [
        input.name,
        input.id,
        input.placeholder,
        input.getAttribute("aria-label"),
        input.getAttribute("data-field"),
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes("qty") || haystack.includes("quantity");
    };

    const handleFocusIn = (event: FocusEvent) => {
      const target = event.target;
      if (!(target instanceof HTMLInputElement)) {
        return;
      }
      if (!shouldAutoSelect(target)) {
        return;
      }
      window.setTimeout(() => {
        if (document.activeElement !== target) {
          return;
        }
        try {
          target.select();
        } catch {
          // Some input types don't support selection.
        }
      }, 0);
    };

    document.addEventListener("focusin", handleFocusIn);
    return () => {
      document.removeEventListener("focusin", handleFocusIn);
    };
  }, []);

  const handleChangePassword = async () => {
    setUserMenuMessage(null);
    setUserMenuError(null);
    if (!authUser?.email) {
      setUserMenuError("No email address found for your account.");
      return;
    }
    setSendingReset(true);
    try {
      await sendPasswordResetEmail(auth, authUser.email);
      setUserMenuMessage("Password reset email sent.");
    } catch (err: any) {
      setUserMenuError(err?.message || "Failed to send password reset email.");
    } finally {
      setSendingReset(false);
    }
  };

  const handleSignOut = async () => {
    setUserMenuMessage(null);
    setUserMenuError(null);
    try {
      await signOutUser();
    } catch (err: any) {
      setUserMenuError(err?.message || "Failed to sign out.");
    } finally {
      setUserMenuOpen(false);
    }
  };

  return (
    <div className="ps-app-shell">
      <aside
        ref={sidebarRef}
        className={`ps-sidebar ${sidebarOpen ? "ps-sidebar--open" : ""} ${
          sidebarCollapsed ? "ps-sidebar--collapsed" : ""
        }`}
      >
        <button
          type="button"
          className="ps-sidebar__collapse"
          onClick={() => setSidebarCollapsed((collapsed) => !collapsed)}
          aria-label={sidebarCollapsed ? "Expand side menu" : "Collapse side menu"}
          aria-expanded={!sidebarCollapsed}
        >
          <span className="ps-sidebar__collapse-arrow" />
        </button>
        <div className="ps-sidebar__brand">
          <img
            className="ps-sidebar__logo-circle"
            src={blackBoxLogo}
            alt="ParcelStock black box logo"
          />
          <div>
            <div className="ps-sidebar__brand-name">ParcelStock</div>
            <div className="ps-sidebar__brand-sub">Operations Console</div>
          </div>
        </div>

        <nav className="ps-sidebar__nav">
          {canViewOperational && (
            <>
              <NavLink
                to="/"
                end
                style={({ isActive }) => ({
                  ...navLinkBase,
                  ...(isActive ? navLinkHomeActive : {}),
                })}
              >
                <span
                  style={{
                    ...navDotStyle,
                    ...(window.location.pathname === "/" ? navDotActiveStyle : {}),
                  }}
                />
                <span>Home</span>
              </NavLink>

              <div className="ps-sidebar__section-label ps-sidebar__section-label--inventory">
                Inventory
              </div>
              <NavLink
                to="/stock"
                style={({ isActive }) => ({
                  ...navLinkBase,
                  ...(isActive ? navLinkActive : {}),
                })}
              >
                <span
                  style={{
                    ...navDotStyle,
                  }}
                />
                <span>Stock Overview</span>
              </NavLink>
              <NavLink
                to="/transfer"
                style={({ isActive }) => ({
                  ...navLinkBase,
                  ...(isActive ? navLinkActive : {}),
                })}
              >
                <span style={navDotStyle} />
                <span>Stock Transfer</span>
              </NavLink>
              <NavLink
                to="/change"
                style={({ isActive }) => ({
                  ...navLinkBase,
                  ...(isActive ? navLinkActive : {}),
                })}
              >
                <span style={navDotStyle} />
                <span>Quick Stock Change</span>
              </NavLink>
              {canViewAdminTools && (
                <NavLink
                  to="/batch-transfer"
                  style={({ isActive }) => ({
                    ...navLinkBase,
                    ...(isActive ? navLinkActive : {}),
                  })}
                >
                  <span style={navDotStyle} />
                  <span>Batch Stock Transfer</span>
                </NavLink>
              )}

              <div className="ps-sidebar__section-label ps-sidebar__section-label--sales">
                Sales
              </div>
              <NavLink
                to="/orders"
                style={({ isActive }) => ({
                  ...navLinkBase,
                  ...(isActive ? navLinkActive : {}),
                })}
              >
                <span style={navDotStyle} />
                <span>Sales Orders</span>
              </NavLink>
              <NavLink
                to="/picks/batch"
                style={({ isActive }) => ({
                  ...navLinkBase,
                  ...(isActive ? navLinkActive : {}),
                })}
              >
                <span style={navDotStyle} />
                <span>Batch Picking</span>
              </NavLink>

              <div className="ps-sidebar__section-label ps-sidebar__section-label--purchasing">
                Purchasing
              </div>
              <NavLink
                to="/purchase"
                end
                style={({ isActive }) => ({
                  ...navLinkBase,
                  ...(isActive ? navLinkActive : {}),
                })}
              >
                <span style={navDotStyle} />
                <span>Purchase Orders</span>
              </NavLink>
              <NavLink
                to="/purchase/receipts"
                style={({ isActive }) => ({
                  ...navLinkBase,
                  ...(isActive ? navLinkActive : {}),
                })}
              >
                <span style={navDotStyle} />
                <span>Recent Receipts</span>
              </NavLink>

              <div className="ps-sidebar__section-label ps-sidebar__section-label--reports">
                Reports
              </div>
              <NavLink
                to="/reports/restock"
                style={({ isActive }) => ({
                  ...navLinkBase,
                  ...(isActive ? navLinkActive : {}),
                })}
              >
                <span style={navDotStyle} />
                <span>Restock Report</span>
              </NavLink>
              <NavLink
                to="/reports/cycle-count"
                style={({ isActive }) => ({
                  ...navLinkBase,
                  ...(isActive ? navLinkActive : {}),
                })}
              >
                <span style={navDotStyle} />
                <span>Cycle Count</span>
              </NavLink>
              <NavLink
                to="/reports/stock"
                style={({ isActive }) => ({
                  ...navLinkBase,
                  ...(isActive ? navLinkActive : {}),
                })}
              >
                <span style={navDotStyle} />
                <span>Stock Reports</span>
              </NavLink>

              <div className="ps-sidebar__section-label ps-sidebar__section-label--admin">
                Admin
              </div>
              <NavLink
                to="/build"
                style={({ isActive }) => ({
                  ...navLinkBase,
                  ...(isActive ? navLinkActive : {}),
                })}
              >
                <span style={navDotStyle} />
                <span>Builds and BOMs</span>
              </NavLink>
              <NavLink
                to="/transactions"
                style={({ isActive }) => ({
                  ...navLinkBase,
                  ...(isActive ? navLinkActive : {}),
                })}
              >
                <span style={navDotStyle} />
                <span>Transactions</span>
              </NavLink>
              <NavLink
                to="/lookups"
                style={({ isActive }) => ({
                  ...navLinkBase,
                  ...(isActive ? navLinkActive : {}),
                })}
              >
                <span style={navDotStyle} />
                <span>Product Lookups</span>
              </NavLink>
              {canViewAdminTools && (
                <>
                  <NavLink
                    to="/admin/marketplaces"
                    style={({ isActive }) => ({
                      ...navLinkBase,
                      ...(isActive ? navLinkActive : {}),
                    })}
                  >
                    <span style={navDotStyle} />
                    <span>Integrations</span>
                  </NavLink>
                  <NavLink
                    to="/admin/users"
                    style={({ isActive }) => ({
                      ...navLinkBase,
                      ...(isActive ? navLinkActive : {}),
                    })}
                  >
                    <span style={navDotStyle} />
                    <span>User Management</span>
                  </NavLink>
                  {isSuperAdmin && (
                    <NavLink
                      to="/admin/sublocations"
                      style={({ isActive }) => ({
                        ...navLinkBase,
                        ...(isActive ? navLinkActive : {}),
                      })}
                    >
                      <span style={navDotStyle} />
                      <span>Sublocation Admin</span>
                    </NavLink>
                  )}
                </>
              )}
            </>
          )}
          {isClient && (
            <>
              <NavLink
                to="/"
                end
                style={({ isActive }) => ({
                  ...navLinkBase,
                  ...(isActive ? navLinkHomeActive : {}),
                })}
              >
                <span
                  style={{
                    ...navDotStyle,
                    ...(window.location.pathname === "/" ? navDotActiveStyle : {}),
                  }}
                />
                <span>Home</span>
              </NavLink>

              <div className="ps-sidebar__section-label ps-sidebar__section-label--inventory">
                Inventory
              </div>
              <NavLink
                to="/stock"
                style={({ isActive }) => ({
                  ...navLinkBase,
                  ...(isActive ? navLinkActive : {}),
                })}
              >
                <span style={navDotStyle} />
                <span>Stock Overview</span>
              </NavLink>

              <div className="ps-sidebar__section-label ps-sidebar__section-label--sales">
                Sales
              </div>
              <NavLink
                to="/orders"
                style={({ isActive }) => ({
                  ...navLinkBase,
                  ...(isActive ? navLinkActive : {}),
                })}
              >
                <span style={navDotStyle} />
                <span>Sales Orders</span>
              </NavLink>
            </>
          )}
        </nav>
      </aside>

      <div className="ps-shell-main" style={{ ["--ps-section-accent" as any]: sectionAccent }}>
        <header className="ps-topbar">
          <button
            type="button"
            className="ps-topbar__menu"
            ref={menuButtonRef}
            onClick={() => setSidebarOpen((open) => !open)}
            aria-label="Toggle navigation"
          >
            <span className="ps-topbar__menu-icon" />
          </button>

          {canViewOperational && (
            <div className="ps-topbar__quick-links">
              <NavLink
                to="/picks/batch"
                className={({ isActive }) =>
                  `ps-topbar__quick-link${isActive ? " ps-topbar__quick-link--active" : ""}`
                }
              >
                Batch Pick
              </NavLink>
              <NavLink
                to="/receiving"
                className={({ isActive }) =>
                  `ps-topbar__quick-link${isActive ? " ps-topbar__quick-link--active" : ""}`
                }
              >
                Receiving
              </NavLink>
              <NavLink
                to="/stock-check/item"
                className={({ isActive }) =>
                  `ps-topbar__quick-link${isActive ? " ps-topbar__quick-link--active" : ""}`
                }
              >
                Item Check
              </NavLink>
              <NavLink
                to="/stock-check/sublocation"
                className={({ isActive }) =>
                  `ps-topbar__quick-link${isActive ? " ps-topbar__quick-link--active" : ""}`
                }
              >
                Sublocation Check
              </NavLink>
            </div>
          )}

          <div style={{ flex: 1 }} />

          <div className="ps-topbar__actions">
            <button type="button" className="ps-icon-button" aria-label="Notifications">
              <span className="ps-icon-dot" />
            </button>
            {authUser && (
              <div style={{ position: "relative" }}>
                <button
                  type="button"
                  className="ps-avatar"
                  ref={userMenuButtonRef}
                  onClick={() => {
                    setUserMenuMessage(null);
                    setUserMenuError(null);
                    setUserMenuOpen((open) => !open);
                  }}
                  aria-label="User menu"
                  style={{ border: "none", cursor: "pointer" }}
                >
                  {avatarLetter}
                </button>
                {userMenuOpen && (
                  <div
                    ref={userMenuRef}
                    style={{
                      position: "absolute",
                      right: 0,
                      top: "calc(100% + 8px)",
                      minWidth: 190,
                      background: theme.colors.surface,
                      border: `1px solid ${theme.colors.secondaryBorder}`,
                      borderRadius: 12,
                      padding: 10,
                      boxShadow: "0 18px 45px rgba(15, 23, 42, 0.12)",
                      zIndex: 20,
                    }}
                  >
                    <div style={{ fontSize: 12, color: theme.colors.muted, marginBottom: 10 }}>
                      {authUser.email}
                    </div>
                    <button
                      type="button"
                      onClick={handleChangePassword}
                      disabled={sendingReset}
                      style={{
                        width: "100%",
                        textAlign: "left",
                        padding: "8px 10px",
                        borderRadius: 10,
                        border: `1px solid ${theme.colors.secondaryBorder}`,
                        background: theme.colors.secondary,
                        cursor: sendingReset ? "not-allowed" : "pointer",
                        marginBottom: 8,
                        fontSize: 13,
                      }}
                    >
                      {sendingReset ? "Sending..." : "Change password"}
                    </button>
                    <button
                      type="button"
                      onClick={handleSignOut}
                      style={{
                        width: "100%",
                        textAlign: "left",
                        padding: "8px 10px",
                        borderRadius: 10,
                        border: `1px solid ${theme.colors.secondaryBorder}`,
                        background: theme.colors.surface,
                        cursor: "pointer",
                        fontSize: 13,
                      }}
                    >
                      Sign out
                    </button>
                    {userMenuMessage && (
                      <div style={{ marginTop: 8, fontSize: 12, color: theme.colors.success }}>
                        {userMenuMessage}
                      </div>
                    )}
                    {userMenuError && (
                      <div style={{ marginTop: 8, fontSize: 12, color: theme.colors.danger }}>
                        {userMenuError}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </header>

        <main className="ps-shell-content">{children ?? <Outlet />}</main>
      </div>
    </div>
  );
};

export default LayoutShell;

